import React from 'react';
import { Play } from 'lucide-react';

/**
 * FeaturedFilmCard
 * A premium story-style card for the "Featured Films" section.
 * - Large thumbnail with subtle zoom on hover
 * - Play overlay pulses in on hover
 * - Emotional headline + one-line story
 * - "Watch Preview" button that opens the shared VideoModal via onPlay handler
 */
const FeaturedFilmCard = ({ film, onPlay }) => {
  return (
    <button
      type="button"
      onClick={() => onPlay(film)}
      data-testid={`featured-film-${film.category}`}
      className="group relative block w-full text-left rounded-2xl overflow-hidden border border-white/10 hover:border-amber-500/50 transition-all duration-500 bg-black focus:outline-none focus:ring-2 focus:ring-amber-500/60"
      aria-label={`Watch ${film.title}`}
    >
      {/* Thumbnail */}
      <div className={`${film.wide ? 'aspect-video' : 'aspect-[16/10]'} overflow-hidden bg-gray-900`}>
        <img
          src={film.thumbnail}
          alt={film.title}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
        />
        {/* Bottom gradient wash for legibility */}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent pointer-events-none" />

        {/* Play button — always visible, glows on hover */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white/10 backdrop-blur-md border border-white/30 flex items-center justify-center transition-all duration-500 group-hover:bg-amber-500 group-hover:border-amber-500 group-hover:scale-110 shadow-2xl">
            <Play size={26} className="text-white group-hover:text-black ml-1 transition-colors" fill="currentColor" />
          </div>
        </div>

        {/* Category chip */}
        <div className="absolute top-4 left-4 px-3 py-1 rounded-full bg-black/60 backdrop-blur-sm border border-amber-500/40 text-amber-300 text-[10px] uppercase tracking-[0.2em] font-semibold">
          {film.categoryLabel}
        </div>
      </div>

      {/* Story caption */}
      <div className="p-6">
        <h3 className="text-white font-editorial text-xl md:text-2xl leading-tight mb-2">
          {film.title}
        </h3>
        <p className="text-gray-400 text-sm md:text-[15px] leading-relaxed mb-5">
          {film.story}
        </p>
        {film.credit && <p className="text-xs italic text-amber-400/80 -mt-3 mb-5">{film.credit}</p>}
        <span className="inline-flex items-center gap-2 text-amber-400 group-hover:text-amber-300 text-sm font-medium">
          <Play size={14} fill="currentColor" />
          Watch Preview
        </span>
      </div>
    </button>
  );
};

export default FeaturedFilmCard;
